import {Injectable} from '@angular/core';
import * as moment from 'moment-timezone';
import {Observable, Subject} from 'rxjs';

import {CurrentSkedTask} from 'src/app/core/model/task';
import {User} from 'src/app/core/model/user';
import {LensSettingsOptions} from 'src/app/lenses/shared/model/lens-setting';
import {TimezoneService} from 'src/app/services/timezone.service';
import {AvailableTaskActions, AvailableTaskActionsItem} from './model/available-task-actions';
import {LensSetting} from './../../lenses/shared/model/lens-setting';
import {TaskStatuses} from './task-statuses';

@Injectable({
  providedIn: 'root',
})
export class CurrentSkedService {
  /** Set by task list before navigating to task detail so detail can run enter animation. */
  navigatingFromTaskList = false;

  failsUserReqsMessage = 'You do not satisfy the necessary task requirements.';
  failsTaskOwnerMessage = 'You are not the owner of this task.';
  subtaskLensMessage = 'You must complete all the subtasks.';

  private notify = new Subject<number>();

  public notifyObservable$: Observable<number> = this.notify.asObservable();

  constructor(private timezoneService: TimezoneService) {}

  public dubaiThemeCardFlipIndex(subtaskIndex: number) {
    if (subtaskIndex || subtaskIndex === 0) {
      this.notify.next(subtaskIndex);
    }
  }

  getAvailableTaskActions(
    task: CurrentSkedTask,
    user: User,
    lensMap: LensSetting,
    skedTimeStatus: string
  ): AvailableTaskActions {
    return {
      [TaskStatuses.Unclaimed]: {disabled: !task.authorizationObject.isAuthorized},
      [TaskStatuses.InProgress]: {disabled: !task.authorizationObject.isAuthorized},
      [TaskStatuses.OnHold]: {disabled: !task.authorizationObject.isAuthorized},
      [TaskStatuses.Cancelled]: {disabled: !task.authorizationObject.isAuthorized},
      [TaskStatuses.Completed]: this.getCompleteTaskActionItem(user, task, lensMap),
    };
  }

  getInProgressTaskActionItem(task: CurrentSkedTask, user: User, skedTimeStatus: string): AvailableTaskActionsItem {
    const passesUserReqs = this.passesUserReqs(task, user);
    // const passesStatus = task.status === TaskStatuses.Unclaimed;

    if (!passesUserReqs) {
      return {disabled: true, message: this.failsUserReqsMessage};
    }

    if (passesUserReqs) {
      const timezone = this.timezoneService.getTimeZone();
      const passesTimeReqs = moment(task.startDateTime).tz(timezone).isBefore(moment().tz(timezone));
      const disabled = !passesTimeReqs || skedTimeStatus === 'future';
      return {
        disabled,
        message: disabled ? `Your ${(task.event && 'event') || 'task'}  is not ready to start yet.` : '',
      };
    }
  }

  passesUserReqs(task: CurrentSkedTask, user: User): boolean {
    return user.requirements.some(r => task.skills?.includes(r)) || task.skills?.length === 0;
  }

  getCancelActionItem(task: CurrentSkedTask, user: User): AvailableTaskActionsItem {
    const passesUserReqs = this.passesUserReqs(task, user);
    const message = passesUserReqs ? '' : this.failsTaskOwnerMessage;

    return {
      disabled: (task.ownerId && user._id !== task.ownerId) || !passesUserReqs,
      message,
    };
  }

  getCompleteTaskActionItem(user: User, task: CurrentSkedTask, lensMap: LensSetting): AvailableTaskActionsItem {
    let message = '';
    const buildActionItem = (passesLensReqs: boolean) => ({
      disabled: !passesLensReqs || !task.authorizationObject.isAuthorized,
      message,
    });
    if (!lensMap || Object.keys(lensMap).length === 0) return buildActionItem(false);

    const passesLensReqs =
      lensMap[LensSettingsOptions.CompleteTasksWithoutSubtaskCompletion]?.active ||
      task?.subtasks?.every(subtask => subtask.complete);

    if (!passesLensReqs) {
      message = this.subtaskLensMessage;
    }

    return buildActionItem(passesLensReqs);
  }
}
